// utils/linkedRecords.js
//
// READING THROUGH A COLLECTION LINK (keepr-api docs/SCHEMA.md § Collection
// links, § Lookups across a link, § When a link ends, § Merged lists): the
// rules every surface that shows another collection's record inside this one
// shares, so a tile, a table row, a lookup chip and the detail pane say the
// same thing about it.
//
//   - A LINKED ROW is a record of collection A in B's merged list
//     (`includeLinked=1`). It carries `linkedFrom: { collection_id, name,
//     icon, color }`, no attribution, and is read-only here: no edit, no
//     delete, no bulk selection. Its home is A — "Open" goes there.
//   - A BORROWED CARD is one of A's cards whose records a link brings into B
//     (`borrowedFrom: { collection: {...} }`, `readOnly: true`). It filters
//     and it is looked up, but no item of it is ever created in B
//     (403 `card_borrowed`).
//   - A lookup id the batch resolve could not return is NOT READABLE (the
//     `X-Not-Readable` header): the chip says "No longer available" instead of
//     going blank or printing an id — the tombstone a link leaves when it ends
//     without copies.

import { useSyncExternalStore } from 'react';

import { ancestorById, familyMemberOf } from './collectionFamily';
import { normalizeId } from './ids';
import { isLinkOnlyReader } from './permissions';
import { getSessionKey, onSessionChange } from './session';

// ------------------------------------------------------------------ linked rows

// A source's name as prose may say it ("Shared from another collection"), and
// whether the API actually named it. Only a NAMED source is ever used as a
// collection's identity (utils/recordQualifier rowSourceIdentity): the
// placeholder is words for a sentence, never a collection's name.
const sourceNameOf = raw => {
	const named = typeof raw === 'string' && raw.trim() !== '';
	return { name: named ? raw : 'another collection', named };
};

/**
 * A linked row's source collection, `{ collectionId, name, named, icon, color }`,
 * or null for B's own rows. `named` is false when the API sent no name (`name`
 * is then the placeholder "another collection", for prose only).
 */
export function linkedFromOf(item) {
	const from = item?.linkedFrom;
	if (!from || typeof from !== 'object') return null;
	const collectionId = normalizeId(from.collection_id);
	if (!collectionId) return null;
	return {
		collectionId,
		...sourceNameOf(from.name),
		icon: from.icon || null,
		color: from.color || null,
	};
}

export const isLinkedRow = item => Boolean(linkedFromOf(item));

/**
 * Where a row in `collection`'s list really lives, when that is somewhere
 * else — the ONE helper every "from" badge reads:
 *
 *   - a linked row (a collection link brought it in): `{ via: 'link', … }`,
 *     read-only here;
 *   - a sub-collection's row in its parent's family list (keepr-api §
 *     Sub-collections): `{ via: 'family', … }`, an ordinary record of the
 *     family — it opens, and is edited, where it lives;
 *   - a parent collection's record a sub-collection's item points at, opened
 *     here by id (the referenced-record read): `{ via: 'parent', … }`,
 *     read-only here, like a linked row.
 *
 * `{ via, collectionId, name, icon, color }`, or null for the list's own rows.
 */
export function rowSourceOf(item, collection = null) {
	const linked = linkedFromOf(item);
	if (linked) return { via: 'link', ...linked };
	const member = familyMemberOf(item, collection);
	// A family row's collection is a sub-collection of the list's own: its
	// parent is named, so two sub-collections called the same are told apart.
	if (member) return { via: 'family', collectionId: member._id, name: member.name, icon: member.icon, color: member.color, parentName: collection?.name || null };
	const above = ancestorById(collection, item?.collection_id);
	if (above) return { via: 'parent', collectionId: above._id, name: above.name, icon: above.icon, color: above.color };
	return null;
}

/** Is this row read-only in `collection`'s list — a linked row, or a parent's record read by reference? */
export const isReadOnlyRow = (item, collection = null) => {
	const via = rowSourceOf(item, collection)?.via;
	return via === 'link' || via === 'parent';
};

/**
 * Where a record lives, for the links that open it: a linked row's source
 * collection, else the item's own `collection_id` (a sub-collection's record
 * in its parent's family list opens in the sub-collection), else
 * `fallbackId` (the list's own — a row that does not say, such as one a
 * share link serves).
 */
export function itemHomeCollectionId(item, fallbackId = null) {
	return linkedFromOf(item)?.collectionId || normalizeId(item?.collection_id) || normalizeId(fallbackId) || null;
}

/** Why a linked row cannot join a selection, be edited or be deleted here. */
export const linkedReadOnlyReason = item => {
	const from = linkedFromOf(item);
	return from ? `Shared from ${from.name} — read-only here.` : null;
};

/**
 * The ⋯ entries a linked row never offers: everything that writes, or that
 * hands the record over as this collection's (sharing it, printing it as
 * ours, quick add from it, its audience, its attachments — which ride the
 * copies switch). Edit, duplicate, relate, history and delete already follow
 * `canEdit`, which a linked row never has.
 */
export const LINKED_ROW_EXCLUDED_ACTIONS = Object.freeze(['quick-add', 'audience', 'attachments', 'share', 'export', 'print', 'unlock', 'correct']);

/**
 * The ⋯ entries to drop for a record read in `collection`: a link-only
 * reader of it (utils/permissions isLinkOnlyReader) is refused everything a
 * linked row never offers — on A's own item page as much as in B's list —
 * plus whatever the caller already excludes.
 */
export function linkReaderExclusions(collection, exclude = []) {
	const base = Array.isArray(exclude) ? exclude : [];
	return isLinkOnlyReader(collection) ? [...new Set([...base, ...LINKED_ROW_EXCLUDED_ACTIONS])] : base;
}

// ------------------------------------------------------------------ borrowed cards

/** A borrowed card's lending collection, `{ collectionId, name, named, icon, color }` (as linkedFromOf), or null. */
export function borrowedFromOf(card) {
	const from = card?.borrowedFrom?.collection;
	if (!from || typeof from !== 'object') return null;
	const collectionId = normalizeId(from.collection_id || from._id);
	if (!collectionId) return null;
	return {
		collectionId,
		...sourceNameOf(from.name),
		icon: from.icon || null,
		color: from.color || null,
	};
}

/** A lender or a linked row's source as RecordBadge's `collection`: its identity when named, else its id alone. */
export const sourceQualifierOf = from => {
	if (!from) return null;
	return from.named === false
		? from.collectionId
		: { _id: from.collectionId, name: from.name, icon: from.icon, color: from.color };
};

export const isBorrowedCard = card => Boolean(borrowedFromOf(card));

/** "from Clinic" — the hint every borrowed card and hop field carries. */
export const fromLabel = name => `from ${name || 'another collection'}`;

// ------------------------------------------------------------------ the merged list

/**
 * Is the merged list on? `param` is the URL's `linked` ('1' | '0' | null).
 * DEFAULT ON when the collection reads from another one — the records a
 * link brings in are what the link is for, and a list that hides them
 * until a toggle is found reads as a link that does nothing. `linked=0`
 * turns it off and is kept in the URL (per collection, shareable); `1` is
 * written only by links that mean it explicitly ("Linked from" on the home).
 */
export function resolveLinkedParam(param, hasIncoming) {
	if (!hasIncoming) return false;
	if (param === '0' || param === 'false') return false;
	return true;
}

/**
 * The merged list narrowed to one source's records: its borrowed cards, as
 * `card in (…)` by id (a borrowed card answers by id — its key may collide
 * with one of B's). Null when the source lends nothing this build can name.
 */
export function linkedSourceListPath(collectionId, sourceId, borrowedCards = []) {
	const cid = normalizeId(collectionId);
	const sid = normalizeId(sourceId);
	if (!cid || !sid) return null;
	const ids = (borrowedCards || [])
		.filter(card => borrowedFromOf(card)?.collectionId === sid)
		.map(card => normalizeId(card?._id))
		.filter(Boolean);
	const params = new URLSearchParams({ linked: '1' });
	if (ids.length === 1) params.set('card', ids[0]);
	else if (ids.length > 1) params.set('q', `card in (${ids.join(', ')})`);
	return `/collections/${cid}/items?${params.toString()}`;
}

// ------------------------------------------------------------------ not readable

export const NOT_READABLE_LABEL = 'No longer available';
export const NOT_READABLE_TOOLTIP = 'This record is no longer shared with this collection';

/** The ids in an `X-Not-Readable` header (comma-separated; absent or empty = none). */
export function parseNotReadableHeader(headers) {
	if (!headers) return [];
	let raw = headers['x-not-readable'];
	if (raw === undefined) {
		const key = Object.keys(headers).find(name => name.toLowerCase() === 'x-not-readable');
		raw = key === undefined ? undefined : headers[key];
	}
	if (typeof raw !== 'string' || !raw.trim()) return [];
	return raw.split(',').map(id => normalizeId(id.trim())).filter(Boolean);
}

// One app-wide registry: a record is unreadable to THIS session, whichever
// surface asked, so every chip for it agrees. An id that later resolves
// (the link came back) leaves it — whichever surface it arrives through: a
// batch resolve, a list page, a /related payload (useLinkedItems' seed pool).
//
// PER SESSION (utils/session.js): the app signs out without a reload, and
// what one account could not read says nothing about the next one. The
// registry is stamped with the session that filled it, reads as empty to any
// other, and is emptied when the signed-in account changes.
const NONE = Object.freeze(new Set());
let notReadable = NONE;
let owner = null;
const listeners = new Set();
const emit = () => listeners.forEach(listener => listener());

// The registry as THIS session sees it (another session's reads as empty).
const current = () => (owner === getSessionKey() ? notReadable : NONE);

function reset() {
	if (notReadable === NONE) return;
	notReadable = NONE;
	owner = null;
	emit();
}

onSessionChange(reset);

export function markNotReadable(ids) {
	const held = current();
	const fresh = (ids || []).map(normalizeId).filter(id => id && !held.has(id));
	if (!fresh.length) return;
	notReadable = new Set([...held, ...fresh]);
	owner = getSessionKey();
	emit();
}

export function markReadable(ids) {
	const held = current();
	const gone = (ids || []).map(normalizeId).filter(id => id && held.has(id));
	if (!gone.length) return;
	const next = new Set(held);
	gone.forEach(id => next.delete(id));
	notReadable = next.size ? next : NONE;
	emit();
}

export const isNotReadable = id => current().has(normalizeId(id));

const subscribe = listener => {
	listeners.add(listener);
	return () => listeners.delete(listener);
};
const snapshot = current;

/** The live set of unreadable ids; a component re-renders when it changes. */
export function useNotReadableIds() {
	return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** Test helper: forget everything. */
export function _resetNotReadable() {
	notReadable = NONE;
	owner = null;
	emit();
}
